import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import useConfiguratorStore from '../store/configuratorStore';

const Container = styled.div`
  margin-top: 1rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-700);
`;

const PriceModeToggle = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 12px;
  padding: 4px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  width: 100%;
  margin-bottom: 1.5rem;
`;

const ModeOption = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: var(--text-sm);
  font-weight: 600;
  border-radius: 9px;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
  text-align: center;
  
  ${props => props.active ? `
    background: linear-gradient(135deg, ${props.isPartner ? 'var(--partner-color)' : 'var(--client-color)'} 0%, ${props.isPartner ? 'var(--partner-dark)' : 'var(--client-dark)'} 100%);
    color: white;
    box-shadow: 0 2px 8px ${props.isPartner ? 'var(--partner-light)' : 'var(--client-light)'};
  ` : `
    color: var(--gray-600);
    &:hover {
      color: var(--gray-800);
      background: var(--gray-50);
    }
  `}
`;

const QuantitySection = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const QuantityInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputLabel = styled.label`
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const QuantityInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const QuantityInput = styled.input`
  width: 160px;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--gray-800);
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: var(--primary-color);
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    transform: translateY(-1px);
  }
  
  &::placeholder {
    color: var(--gray-400);
    font-weight: 500;
  }
`;

const AddToQuoteButton = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  background: ${props => props.addedToQuote ? 
    'var(--partner-color)' : 
    'var(--primary-color)'};
  color: white;
  border-radius: 12px;
  font-size: var(--text-sm);
  font-weight: 600;
  border: none;
  box-shadow: 0 2px 8px ${props => props.addedToQuote ? 
    'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.2)'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  margin-bottom: 0.25rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.addedToQuote ? 
      'rgba(34, 197, 94, 0.3)' : 'rgba(59, 130, 246, 0.3)'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const VolumeTable = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #f8fafc;
  padding: 0.75rem 1rem;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
`;

const TableBody = styled.div`
  max-height: 300px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  cursor: pointer;
  
  ${props => props.selected && `
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%);
    border-color: var(--primary-color);
  `}
  
  &:hover {
    background: #f8fafc;
    transform: translateX(2px);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const QuantityRange = styled.div`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-800);
`;

const PriceWithDiscount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-800);
`;

const DiscountBadge = styled.span`
  background: linear-gradient(135deg, var(--partner-color) 0%, var(--partner-dark) 100%);
  color: white;
  font-size: var(--text-xs);
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(34, 197, 94, 0.2);
`;

const NoQuantitiesMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--gray-500);
  font-style: italic;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

// Helper to format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

const QuantitySelector = React.forwardRef((props, ref) => {
  const {
    selectedMainCategory,
    selectedProduct,
    selectedQuantity,
    organizedProducts,
    setSelectedQuantity,
    priceMode,
    togglePriceMode,
    fairModeEnabled,
    calculateQuote,
    addToQuote
  } = useConfiguratorStore();
  
  const [customQuantity, setCustomQuantity] = React.useState('');
  const [addedToQuote, setAddedToQuote] = React.useState(false);
  
  React.useEffect(() => {
    setCustomQuantity('');
  }, [selectedProduct]);
  
  const productObject = selectedMainCategory && selectedProduct && organizedProducts[selectedMainCategory] 
    ? organizedProducts[selectedMainCategory].find(p => p.id === selectedProduct)
    : null;
    
  const determineApplicableTier = React.useCallback((quantity) => {
    if (!quantity || isNaN(quantity) || quantity <= 0 || !productObject || !productObject.quantities) return null;
    
    const parsedTiers = productObject.quantities.map(tier => {
      const range = tier.id.split('-');
      return {
        ...tier,
        min: parseInt(range[0], 10),
        max: range.length > 1 ? parseInt(range[1], 10) : Infinity
      };
    }).sort((a, b) => a.min - b.min);
    
    for (const tier of parsedTiers) {
      if (quantity >= tier.min && quantity <= tier.max) {
        return tier;
      }
    }
    
    return parsedTiers[parsedTiers.length - 1];
  }, [productObject]);
  
  if (!productObject || !productObject.quantities || productObject.quantities.length === 0) {
    return <NoQuantitiesMessage>No quantity options available for this product</NoQuantitiesMessage>;
  }
  
  const handleQuantityChange = (e) => {
    const value = e.target.value;
    setCustomQuantity(value);
    
    if (value === '') {
      return;
    }
    
    const quantity = parseInt(value, 10);
    if (!isNaN(quantity) && quantity > 0) {
      const tier = determineApplicableTier(quantity);
      if (tier) {
        setSelectedQuantity(tier.id);
        
        setTimeout(() => {
          const updateQuoteWithCustomQuantity = () => {
            const state = useConfiguratorStore.getState();
            const { selectedProduct, selectedQuantity, selectedMainCategory, organizedProducts } = state;
            
            if (!selectedProduct || !selectedMainCategory || !selectedQuantity) return;
            
            const products = organizedProducts[selectedMainCategory] || [];
            const product = products.find(p => p.id === selectedProduct);
            if (!product) return;
            
            const quantityTier = product.quantities.find(q => q.id === selectedQuantity);
            if (!quantityTier) return;
            
            const unitPrice = quantityTier.price;
            const subtotal = unitPrice * quantity;
            const lineItems = [{ name: product.name, price: unitPrice, quantity: quantity, totalPrice: subtotal }];
            const tax = subtotal * 0.21;
            const total = subtotal + tax;
            
            useConfiguratorStore.setState({ quote: { subtotal, tax, total, lineItems } });
          };
          
          updateQuoteWithCustomQuantity();
        }, 50);
      }
    }
  };
  
  const handleQuantitySelect = (quantityId) => {
    setSelectedQuantity(quantityId);
    setCustomQuantity('');
    
    setTimeout(() => calculateQuote(), 50);
  };
  
  const handlePriceModeToggle = () => {
    togglePriceMode();
    setTimeout(() => calculateQuote(), 100);
  };
  
  const calculateDiscount = (price) => {
    if (!productObject.quantities || productObject.quantities.length === 0) return 0;
    const basePrice = productObject.quantities[0].price;
    if (!basePrice || !price) return 0;
    return Math.round((1 - price / basePrice) * 100);
  };
  
  const handleAddToQuote = () => {
    if (selectedQuantity) {
      setAddedToQuote(true);
      addToQuote();
      setCustomQuantity('');
      setTimeout(() => setAddedToQuote(false), 2000);
    }
  };

  return (
    <Container ref={ref}>
      <QuantitySection>
        <QuantityInputGroup>
          <InputLabel>Exact Quantity</InputLabel>
          <QuantityInputWrapper>
            <QuantityInput 
              type="number" 
              min="1"
              placeholder="Enter amount" 
              value={customQuantity}
              onChange={handleQuantityChange}
            />
          </QuantityInputWrapper>
        </QuantityInputGroup>
        
        {selectedQuantity && (
          <AddToQuoteButton
            whileTap={{ scale: 0.98 }}
            onClick={handleAddToQuote}
            addedToQuote={addedToQuote}
            disabled={!selectedQuantity}
          >
            {addedToQuote ? (
              <>
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500 }}
                >
                  ✓
                </motion.span>
                Added to Quote!
              </>
            ) : (
              <>
                <span>+</span>
                Add to Quote
              </>
            )}
          </AddToQuoteButton>
        )}
      </QuantitySection>
      
      <VolumeTable>
        <TableHeader>
          <div>Quantity Range</div>
          <div>Price per Unit</div>
        </TableHeader>
        <TableBody>
          {productObject.quantities.map((quantity, index) => {
            const discount = index > 0 ? calculateDiscount(quantity.price) : 0;
            const isSelected = selectedQuantity === quantity.id && !customQuantity;
            
            return (
              <TableRow 
                key={quantity.id}
                onClick={() => handleQuantitySelect(quantity.id)}
                selected={isSelected}
              >
                <QuantityRange>{quantity.name}</QuantityRange>
                <PriceWithDiscount>
                  {formatCurrency(quantity.price)}
                  {discount > 0 && (
                    <DiscountBadge>-{discount}%</DiscountBadge>
                  )}
                </PriceWithDiscount>
              </TableRow>
            );
          })}
        </TableBody>
      </VolumeTable>
    </Container>
  );
});

export default QuantitySelector;
